/* ==========================================================================
   FLRC — Size guide units (cm / in)
   ==========================================================================
   Charts are authored once, in metric, in the size guide section. This file
   gives each one an IN / CM toggle instead of printing both systems side by
   side.

   Columns are classified by their header unit:
     (cm) (kg)            metric
     (in) (ft) (lb)       imperial — or no unit with ft/in values (5'7")
     anything else        neutral, always shown (Size)
   A metric column with an imperial twin of the same name (Height (cm) and
   Height) swaps with it, so hand-written imperial values win. A column with no
   twin is converted here: height to feet and inches, other lengths to the
   nearest half inch, weight to whole pounds.

   Default: a saved choice wins; otherwise inches for visitors in the US (by
   Shopify's localization country, or a US time zone when the store falls back
   to its home market), cm for everyone else.
   ========================================================================== */

const STORAGE_KEY = 'flrc:size-units';
const IMPERIAL_COUNTRIES = ['US', 'LR', 'MM'];
const US_TIME_ZONE =
  /^(America\/(New_York|Chicago|Denver|Los_Angeles|Phoenix|Anchorage|Juneau|Sitka|Metlakatla|Yakutat|Nome|Adak|Boise|Detroit|Menominee|Indianapolis|Louisville|Puerto_Rico|Indiana\/.+|Kentucky\/.+|North_Dakota\/.+)|Pacific\/(Honolulu|Guam|Saipan|Pago_Pago)|US\/.+)$/;

const readSaved = () => {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === 'metric' || value === 'imperial' ? value : null;
  } catch {
    return null;
  }
};

const save = (units) => {
  try {
    localStorage.setItem(STORAGE_KEY, units);
  } catch {
    // Private mode or blocked storage: the choice just won't outlive the page.
  }
};

const detect = (country) => {
  if (IMPERIAL_COUNTRIES.includes((country || '').toUpperCase())) return 'imperial';
  let zone = '';
  try {
    zone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {}
  if (zone) return US_TIME_ZONE.test(zone) ? 'imperial' : 'metric';
  return /-US$/i.test(navigator.language || '') ? 'imperial' : 'metric';
};

let current = null;
const currentUnits = (country) => (current ??= readSaved() || detect(country));

/* ---- Conversion ------------------------------------------------------- */

const NUMBER = /\d+(?:\.\d+)?/g;
const FEET_INCHES = /(\d+)\s*['′]\s*(?:(\d+(?:\.\d+)?)\s*(?:["″]|''))?/g;

const half = (n) => {
  const r = Math.round(n * 2) / 2;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
};

const feet = (inches) => `${Math.floor(inches / 12)}'${inches % 12}"`;

// Height bands are whole cm and run edge to edge (M 179–187, MT 188–191), so
// rounding both ends lands two sizes on one inch (187 and 188 both read 6'2").
// A band's top is floored instead and its bottom starts one inch past the top
// of the band below — the rule the hand-written charts already follow
// (S to 5'10", M from 5'11"). ">191" floors like a top; a lone value rounds.
const HEIGHT = /([<>]\s*)?(\d+(?:\.\d+)?)(?:(\s*[–—-]\s*)(\d+(?:\.\d+)?))?/g;
const topInch = (cm) => Math.floor(Number(cm) / 2.54);
const bottomInch = (cm) => Math.floor((Number(cm) - 1) / 2.54) + 1;

const cmToFeet = (_, sign, from, dash, to) => {
  if (dash) {
    let low = bottomInch(from);
    let high = topInch(to);
    if (low > high) low = high = Math.round(Number(from) / 2.54);
    return `${feet(low)}${dash}${feet(high)}`;
  }
  if (sign?.trim() === '>') return `${sign}${feet(topInch(from))}`;
  if (sign) return `${sign}${feet(bottomInch(from))}`;
  return feet(Math.round(Number(from) / 2.54));
};

const convert = {
  height: (text) => text.replace(HEIGHT, cmToFeet),
  cm: (text) => text.replace(NUMBER, (n) => half(Number(n) / 2.54)),
  kg: (text) => text.replace(NUMBER, (n) => String(Math.round(Number(n) * 2.20462))),
  feet: (text) =>
    text.replace(FEET_INCHES, (_, ft, inches) => String(Math.round((Number(ft) * 12 + Number(inches || 0)) * 2.54))),
  in: (text) => text.replace(NUMBER, (n) => half(Number(n) * 2.54)),
  lb: (text) => text.replace(NUMBER, (n) => String(Math.round(Number(n) / 2.20462))),
};

const baseName = (header) => header.replace(/\([^)]*\)/g, '').trim();

// Returns { system, kind } for a column, from its header and its cells.
const classify = (header, cells) => {
  const unit = (header.match(/\(([^)]*)\)/) || [])[1]?.trim().toLowerCase() || '';
  const isHeight = /height/i.test(header);
  if (unit === 'cm') return { system: 'metric', kind: isHeight ? 'height' : 'cm' };
  if (unit === 'kg') return { system: 'metric', kind: 'kg' };
  if (unit === 'lb' || unit === 'lbs') return { system: 'imperial', kind: 'lb' };
  if (unit === 'ft' || unit === 'ft/in') return { system: 'imperial', kind: 'feet' };
  if (unit === 'in' || unit === 'inch' || unit === 'inches') return { system: 'imperial', kind: 'in' };
  if (!unit && cells.some((c) => /\d\s*['′]/.test(c))) return { system: 'imperial', kind: 'feet' };
  return { system: null, kind: null };
};

const HEADER_UNITS = {
  height: { metric: 'cm', imperial: 'ft/in' },
  feet: { metric: 'cm', imperial: 'ft/in' },
  cm: { metric: 'cm', imperial: 'in' },
  in: { metric: 'cm', imperial: 'in' },
  kg: { metric: 'kg', imperial: 'lb' },
  lb: { metric: 'kg', imperial: 'lb' },
};

// Annotates a table once. Every cell that changes gets data-metric and
// data-imperial text; every cell that only belongs to one system gets
// data-unit-only. Idempotent, and survives outerHTML copies into the modal.
const prepare = (table) => {
  if (table.hasAttribute('data-units-ready')) return;
  const rows = Array.from(table.rows);
  const head = table.tHead?.rows[0];
  if (!head) return;
  const headers = Array.from(head.cells).map((c) => c.textContent.trim());
  const columnCells = (i) => rows.filter((r) => r !== head).map((r) => r.cells[i]).filter(Boolean);

  const columns = headers.map((h, i) => ({
    header: h,
    base: baseName(h).toLowerCase(),
    ...classify(h, columnCells(i).map((c) => c.textContent)),
  }));

  columns.forEach((col, i) => {
    if (!col.system) return;
    const other = col.system === 'metric' ? 'imperial' : 'metric';
    const twin = columns.some((c) => c.system === other && c.base === col.base);
    const cells = [head.cells[i], ...columnCells(i)];

    if (twin) {
      cells.forEach((cell) => cell.setAttribute('data-unit-only', col.system));
      head.cells[i].textContent = `${baseName(col.header)} (${HEADER_UNITS[col.kind][col.system]})`;
      return;
    }

    const label = HEADER_UNITS[col.kind];
    head.cells[i].dataset[col.system] = col.header;
    head.cells[i].dataset[other] = `${baseName(col.header)} (${label[other]})`;
    columnCells(i).forEach((cell) => {
      const text = cell.textContent.trim();
      cell.dataset[col.system] = text;
      cell.dataset[other] = convert[col.kind](text);
    });
  });

  table.setAttribute('data-units-ready', '');
};

// Applies the current units to every chart under `root`.
const apply = (root = document, units = currentUnits()) => {
  root.querySelectorAll('[data-sizeguide-chart] table').forEach((table) => {
    prepare(table);
    table.querySelectorAll('[data-metric]').forEach((cell) => {
      cell.textContent = cell.dataset[units];
    });
    table.querySelectorAll('[data-unit-only]').forEach((cell) => {
      cell.hidden = cell.dataset.unitOnly !== units;
    });
  });
  document.querySelectorAll('flrc-unit-toggle').forEach((toggle) => toggle.sync(units));
};

const set = (units) => {
  current = units;
  save(units);
  apply(document, units);
};

/* ---- Toggle ----------------------------------------------------------- */

class FlrcUnitToggle extends HTMLElement {
  connectedCallback() {
    currentUnits(this.dataset.country);
    this.buttons = Array.from(this.querySelectorAll('button[data-units]'));
    this.buttons.forEach((button) => {
      button.addEventListener('click', () => set(button.dataset.units));
    });
    apply();
  }

  sync(units) {
    this.dataset.units = units;
    this.buttons?.forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.units === units));
    });
  }
}

if (!customElements.get('flrc-unit-toggle')) {
  customElements.define('flrc-unit-toggle', FlrcUnitToggle);
}

window.FlrcSizeUnits = { apply: (root) => apply(root) };
